/**
 * D-EZ-111 — TODAY'S STORYLINES: OPENED BEFORE THE GREEN, PAID OFF AT THE FLAG.
 *
 * A real broadcast opens by telling you what to watch: the rivalry, the man
 * chasing his first win, the points fight, who won here last year. Then it
 * comes back to those threads when the race touches them, and the wrap tells
 * you how each one came out. That arc — set up, callback, resolution — is what
 * made Howard say the game "has no stories" when it was missing.
 *
 * THE RULES:
 *   • Storylines are chosen from history UP TO THE PREVIOUS RACE (the booth's
 *     league, `league.ts`) plus the starting grid. Nothing in this race is read
 *     when choosing them.
 *   • Every name and number in a line is also written down as a Fact.
 *   • Choice is a weighted, hash-rotated pick (never a die), with a variety
 *     rule: one storyline per kind and no driver in two of them.
 *   • The wrap reads the FINISHED race only — it airs at the flag.
 */

import type { FinishEntry } from '../race.ts';
import type { Fact } from './researcher.ts';
import type { BoothLeague } from './league.ts';
import { hottestRivalries, rivalryBetween, type RivalryMoment, type RivalryRecord } from '../people/rivalry.ts';
import { cap, fact, fillSlots, ordWord, plural, rotate, spokenTrack, surname, whenPhrase, type Moment } from './kit.ts';
import { hash as stableHash } from './kit.ts';

export type StoryKind =
  | 'rivalry'
  | 'first-win'
  | 'drought'
  | 'streak'
  | 'defending'
  | 'track'
  | 'points'
  | 'bubble'
  | 'playoff'
  | 'champion'
  | 'last-week'
  | 'milestone'
  | 'pole';

export interface Storyline {
  key: string;
  kind: StoryKind;
  /** The drivers it is about, most central first. */
  ids: string[];
  weight: number;
  /** How the booth opens it. */
  text: string;
  facts: Fact[];
  tpl: string;
  /** Numbers the wrap needs (the drought length, the margin…). */
  n?: number;
}

export interface PreRace {
  /** Starting grid, pole first. Known before the green. */
  order: readonly string[];
  nameOf: (id: string) => string;
  /** The short name — the surname unless two share it. Defaults to the surname. */
  shortOf?: (id: string) => string;
  /** The physical track today, to say "here". */
  venue: string | null;
}

// ---------------------------------------------------------------------------
// Two drivers' history, as a booth would say it
// ---------------------------------------------------------------------------

const HEATED = (m: RivalryMoment) => m.kind !== 'hatchet' && m.kind !== 'lead-pass' && m.kind !== 'words';

/** The moment worth citing: the latest one at this track, else the worst, else the latest. */
export function citedMoment(r: RivalryRecord, venue: string | null): RivalryMoment | null {
  const heated = r.moments.filter(HEATED);
  const here = venue ? [...heated].reverse().find((m) => m.venue === venue) : undefined;
  return here ?? r.worst ?? heated[heated.length - 1] ?? r.firstSpark ?? null;
}

/**
 * One past-tense clause for a remembered moment: "Ackerd put Ballew in the
 * wall here last season". Names, track and season come off the moment.
 */
export function momentClause(m: RivalryMoment, nameOf: (id: string) => string, now: Moment, venue: string | null, shortOf?: (id: string) => string): { text: string; facts: Fact[] } {
  const short = shortOf ?? ((x: string) => surname(nameOf(x)));
  const A = short(m.aggressorId ?? '');
  const B = short(m.victimId ?? '');
  const here = venue !== null && m.venue === venue;
  const where = here ? 'here' : `at ${spokenTrack(m.trackName)}`;
  const when = whenPhrase(m, now);
  const at = `${where} ${when}`;
  let text: string;
  switch (m.kind) {
    case 'wrecked':
      text = m.detail === 'feud-out' || m.detail === 'fume-out' ? `${A} wrecked ${B} out of the race ${at}` : m.detail === 'self-out' ? `${A} went after ${B} ${at} and wrecked himself` : `${A} put ${B} in the wall ${at}`;
      break;
    case 'duel':
      text = `${A} beat ${B} in a duel ${at} and left him in trouble`;
      break;
    case 'duel-failed':
      text = `${A} went after ${B} ${at} and came off worse`;
      break;
    case 'feud-flare':
      text = `their feud boiled over ${at}`;
      break;
    case 'shared-wreck':
      text = `${A} and ${B} were caught in the same crash ${at}`;
      break;
    case 'battle':
      text = `${A} and ${B} went at each other ${m.detail ?? 'again and again'} times ${at}`;
      break;
    case 'photo-finish':
      text = `${A} beat ${B} to the line ${at}`;
      break;
    case 'late-pass-win':
      text = `${A} passed ${B} late to win ${at}`;
      break;
    case 'lead-pass':
      text = `${A} took the lead from ${B} ${at}`;
      break;
    case 'feud-start':
      text = `a feud between ${A} and ${B} went on the list ${when}`;
      break;
    case 'seat':
      text = `${A} took over the ride ${B} had ${when}`;
      break;
    default:
      text = `${A} and ${B} tangled ${at}`;
  }
  const facts = [
    fact('rivalry', `${m.text} (${spokenTrack(m.trackName)}, season ${m.seasonNumber}${m.raceNumber ? `, race ${m.raceNumber}` : ''})`, m.seasonNumber, [m.aggressorId, m.victimId].filter((x): x is string => !!x)),
  ];
  if (m.detail && /^\d+$/.test(m.detail)) facts.push(fact('rivalry', `${m.detail} times`, Number(m.detail)));
  return { text, facts };
}

const STATE_TAIL: Record<string, string[]> = {
  boiling: ["and that one's boiling", "and it's been boiling ever since", "and neither one's let it go", 'and it has only gotten worse', "and that's about as hot as it gets"],
  hot: ["and it's still hot", "and it hasn't cooled off", "and there's plenty left in that one", 'and nobody has forgotten', 'and it still has some heat in it'],
  simmering: ["and it's been simmering", "and it's still simmering", "and it hasn't gone away", 'and it never quite went away', 'and that one is still on the stove'],
};

/** The stakes card's pair note, and the booth's callback: one sentence. */
export function pairHistory(
  r: RivalryRecord,
  nameOf: (id: string) => string,
  now: Moment,
  venue: string | null,
  key: string,
  seq: number,
  shortOf?: (id: string) => string,
): { text: string; facts: Fact[]; tpl: string } | null {
  const m = citedMoment(r, venue);
  if (!m || !m.aggressorId || !m.victimId) return null;
  const c = momentClause(m, nameOf, now, venue, shortOf);
  const tails = STATE_TAIL[r.state] ?? [];
  const t = tails.length ? rotate(tails, `tail|${key}|${r.a}|${r.b}`, seq) : null;
  const text = `${cap(c.text)}${t ? `, ${t.item}` : ''}.`;
  return { text, facts: c.facts, tpl: `pair-${m.kind}${t ? `#${t.index}` : ''}` };
}

// ---------------------------------------------------------------------------
// The open
// ---------------------------------------------------------------------------

interface Draft extends Omit<Storyline, 'tpl' | 'text'> {
  bank: string[];
  slots: Record<string, string | number>;
  extra?: string;
}

export function openStorylines(league: BoothLeague | null, pre: PreRace, seq: number, max = 3): Storyline[] {
  const entered = new Set(pre.order);
  const grid = new Map(pre.order.map((id, i) => [id, i + 1]));
  const name = pre.nameOf;
  const short = pre.shortOf ?? ((x: string) => surname(name(x)));
  const drafts: Draft[] = [];
  const now: Moment = { seasonNumber: league?.seasonNumber ?? 0, raceNumber: league?.raceNumber ?? 0 };
  const s = league?.season ?? null;

  // ---- rivalries in this field --------------------------------------------
  if (league?.people) {
    for (const r of hottestRivalries(league.people.book, 12)) {
      if (!entered.has(r.a) || !entered.has(r.b) || r.teammates) continue;
      const m = citedMoment(r, pre.venue);
      if (!m || !m.aggressorId || !m.victimId) continue;
      const c = momentClause(m, name, now, pre.venue, short);
      const [x, y] = m.aggressorId === r.a ? [r.a, r.b] : [r.b, r.a];
      const h2h = r.headToHead;
      const aheadA = x === r.a ? h2h.aAhead : h2h.bAhead;
      const aheadB = x === r.a ? h2h.bAhead : h2h.aAhead;
      const heat = r.state === 'boiling' ? 9 : r.state === 'hot' ? 7 : 5;
      const here = pre.venue !== null && m.venue === pre.venue;
      drafts.push({
        key: `rivalry|${r.a}|${r.b}`,
        kind: 'rivalry',
        ids: [x, y],
        weight: heat + (here ? 3 : 0) + (r.trend === 'rising' ? 1 : 0),
        facts: [
          ...c.facts,
          fact('rivalry', `${r.aName} and ${r.bName}: ${r.state}`, Math.round(r.heat), [r.a, r.b]),
          ...(h2h.races >= 3 ? [fact('rivalry', `${name(x)} ahead of ${name(y)} in ${aheadA} of ${h2h.races} races; ${name(y)} ahead in ${aheadB}`, h2h.races, [x, y])] : []),
        ],
        bank: [
          'Keep {a} and {b} in your eye line today. {Clause}.',
          '{A} and {B} are both in this field, and {clause}.',
          'Here is the one with history in it: {a} and {b}. {Clause}.',
          '{Clause}. {A} and {B} are back on the same racetrack today.',
          'The one I want to watch is {a} and {b}. {Clause}.',
          '{A} and {B} again. {Clause}.',
          'Remember {a} and {b}? {Clause}.',
          "There's some bad blood in this field. {Clause}.",
          '{A} versus {B}, one more time. {Clause}.',
        ],
        slots: { a: short(x), b: short(y), A: name(x), B: name(y), clause: c.text, Clause: cap(c.text) },
        extra: h2h.races >= 3 && aheadA !== aheadB ? `${short(aheadA > aheadB ? x : y)} has finished ahead in ${Math.max(aheadA, aheadB)} of their ${h2h.races} races together.` : undefined,
      });
    }
  }

  if (s) {
    // ---- the points ---------------------------------------------------------
    const L = s.pointsLeader;
    const second = league?.table[1];
    if (L && second && s.raceNumber > 2) {
      const tight = L.marginToSecond <= 15;
      drafts.push({
        key: `points|${L.driverId}`,
        kind: 'points',
        ids: [L.driverId, second.driverId],
        weight: tight ? 7 : 4,
        n: L.marginToSecond,
        facts: [
          fact('season', `${L.name} leads the standings by ${L.marginToSecond} over ${second.name}`, L.marginToSecond, [L.driverId, second.driverId]),
        ],
        bank: L.marginToSecond === 0
          ? ['{L} and {S} come in dead even at the top of the standings.', "There's nothing between {L} and {S} in the points coming in. Tied."]
          : tight
            ? ['{L} leads the standings, but only by {m} over {S}.', "It's tight at the top: {L} by {m} over {S}.", "{m} points. That's all that separates {L} and {S} coming in.", '{S} is {m} behind {L} in the points, and a good day changes that.', 'The points fight is close. {L} leads {S} by {m}.', '{L} on top of the standings, {S} {m} back. Not much room there.']
            : ['{L} comes in leading the standings by {m} over {S}.', '{L} has a {m}-point cushion on {S} coming in.', '{S} is chasing {L} in the points, {m} back.', "{L}'s the man to catch in the standings, {m} up on {S}.", 'In the standings it is {L}, then {S}, {m} points apart.'],
        slots: { L: L.name, S: second.name, m: L.marginToSecond },
      });
    }

    // ---- the playoff picture ---------------------------------------------
    const p = s.playoff;
    if (p?.phase === 'regular-season' && p.racesToCut <= 8 && p.cutLine) {
      const out = p.bubble.find((b) => b.side === 'out' && entered.has(b.driverId));
      if (out && entered.has(p.cutLine.driverId)) {
        drafts.push({
          key: `bubble|${p.cutLine.driverId}|${out.driverId}`,
          kind: 'bubble',
          ids: [p.cutLine.driverId, out.driverId],
          weight: 6 + (8 - p.racesToCut) / 2,
          n: -out.gap,
          facts: [
            fact('season', `${p.cutLine.name} holds the last playoff spot on points`, p.cutLine.rank, [p.cutLine.driverId]),
            fact('season', `${out.name} is ${-out.gap} points outside the playoff cut`, -out.gap, [out.driverId]),
            fact('season', `${plural(p.racesToCut, 'race')} to the playoff cut, this one included`, p.racesToCut),
          ],
          bank: [
            '{In} holds the last playoff spot on points, and {Out} is {gap} back with {k} to the cut.',
            'Watch the cut line: {Out} sits {gap} points outside it, {In} is the man he has to catch. {k} to go.',
            '{k} to the playoff cut, this one included. {Out} is {gap} points short of {In}.',
          ],
          slots: { In: p.cutLine.name, Out: out.name, gap: -out.gap, k: plural(p.racesToCut, 'race') },
        });
      }
    }
    if (p?.phase === 'playoff' && p.alive.length > 0) {
      drafts.push({
        key: `playoff|${p.alive.length}`,
        kind: 'playoff',
        ids: p.cutLine ? [p.cutLine.driverId] : [],
        weight: 7,
        facts: [
          fact('season', `${plural(p.alive.length, 'driver')} still alive for the title`, p.alive.length),
          ...(p.cutLine ? [fact('season', `${p.cutLine.name} on the cut line`, p.cutLine.rank, [p.cutLine.driverId])] : []),
        ],
        bank: p.cutLine
          ? ['{n} drivers are still alive for the title, and {cut} sits right on the cut line.', 'Playoff racing. {n} still alive, and {cut} is the man on the bubble.']
          : ['{n} drivers are still alive for the title.'],
        slots: { n: p.alive.length, cut: p.cutLine?.name ?? '' },
      });
    }

    // ---- careers -----------------------------------------------------------
    for (const m of s.milestonesInPlay) {
      if (!entered.has(m.driverId)) continue;
      if (m.kind === 'first-win' && m.n >= 10) {
        drafts.push({
          key: `first-win|${m.driverId}`, kind: 'first-win', ids: [m.driverId], weight: 4 + Math.min(4, m.n / 15) + ((grid.get(m.driverId) ?? 99) <= 10 ? 1 : 0), n: m.n,
          facts: [fact('history', `${m.name} is winless in ${m.n} career starts`, m.n, [m.driverId])],
          bank: ['{N} is {n} starts into his career and still looking for that first win.', '{n} starts, no wins. {N} would love to change that today.', 'Somebody is going to get a first win one of these weeks. {N} has waited {n} starts.', '{N} has made {n} starts without a win.'],
          slots: { N: m.name, n: m.n },
        });
      }
      if (m.kind === 'drought' && m.n >= 20) {
        drafts.push({
          key: `drought|${m.driverId}`, kind: 'drought', ids: [m.driverId], weight: 3 + Math.min(3, m.n / 20), n: m.n,
          facts: [fact('history', `${m.name} has gone ${m.n} starts without a win`, m.n, [m.driverId])],
          bank: ["{N} hasn't won in {n} starts.", "It's been {n} starts since {N} last won.", '{n} starts and counting since {N} was in victory lane.'],
          slots: { N: m.name, n: m.n },
        });
      }
      if (m.kind === 'nth-win' && m.n >= 5) {
        drafts.push({
          key: `milestone|${m.driverId}`, kind: 'milestone', ids: [m.driverId], weight: 3, n: m.n,
          facts: [fact('history', `a win would be career win ${m.n} for ${m.name}`, m.n, [m.driverId])],
          bank: ['One more win would be number {n} for {N}.', '{N} is sitting one short of {n} career wins.'],
          slots: { N: m.name, n: m.n },
        });
      }
    }
    for (const st of s.activeStreaks) {
      if (!entered.has(st.driverId) || st.kind !== 'wins') continue;
      drafts.push({
        key: `streak|${st.driverId}`, kind: 'streak', ids: [st.driverId], weight: 8 + st.length, n: st.length,
        facts: [fact('history', `${st.name} has won ${st.length} in a row`, st.length, [st.driverId])],
        bank: ['{N} has won {n} straight. Who stops him?', '{n} in a row for {N}. Everybody else is racing for a way to end it.', '{N} comes in on a {n}-race winning streak.'],
        slots: { N: st.name, n: st.length },
      });
    }
    const d = s.defendingRaceWinner;
    if (d && entered.has(d.driverId)) {
      const when = whenPhrase({ seasonNumber: d.seasonNumber, raceNumber: 1 }, { seasonNumber: s.seasonNumber, raceNumber: 99 });
      drafts.push({
        key: `defending|${d.driverId}`, kind: 'defending', ids: [d.driverId], weight: 4,
        facts: [fact('history', `${d.name} won this race in season ${d.seasonNumber}`, d.seasonNumber, [d.driverId])],
        bank: ["{N} won this race {when}, and he's back to defend it.", 'The last time they ran this one, {N} won it. That was {when}.', '{N} is the defending winner of this race, from {when}.'],
        slots: { N: d.name, when },
      });
    }
    const tw = s.trackWinners.find((w) => w.wins >= 2 && entered.has(w.driverId) && w.driverId !== d?.driverId);
    if (tw) {
      drafts.push({
        key: `track|${tw.driverId}`, kind: 'track', ids: [tw.driverId], weight: 3 + tw.wins / 2, n: tw.wins,
        facts: [fact('history', `${tw.name} has ${tw.wins} wins at this track`, tw.wins, [tw.driverId])],
        bank: ['{N} has won {n} times at this track.', '{N} knows the way around this place. {n} wins here.', '{N} has been to victory lane here {n} times.'],
        slots: { N: tw.name, n: tw.wins },
      });
    }
    const c = s.defendingChampion;
    if (c && entered.has(c.driverId)) {
      drafts.push({
        key: `champion|${c.driverId}`, kind: 'champion', ids: [c.driverId], weight: s.raceNumber === 1 ? 8 : 2,
        facts: [fact('season', `${c.name} is the season ${c.seasonNumber} champion`, c.seasonNumber, [c.driverId])],
        bank: s.raceNumber === 1
          ? ['{N} starts his title defence today as the season {k} champion.', "The champion's back. {N} won it all in season {k}, and today it starts over."]
          : ['{N} is the defending champion.', "Don't forget the defending champion, {N}."],
        slots: { N: c.name, k: c.seasonNumber },
      });
    }
  }

  // ---- last week ----------------------------------------------------------
  const lw = league?.lastRace;
  if (lw?.winnerId && lw.winnerName && entered.has(lw.winnerId)) {
    drafts.push({
      key: `last-week|${lw.winnerId}`, kind: 'last-week', ids: [lw.winnerId], weight: 3,
      facts: [fact('history', `${lw.winnerName} won last week's ${lw.raceName} at ${spokenTrack(lw.trackName)}`, 1, [lw.winnerId])],
      bank: ['{N} comes in off the win at {track} last week.', '{N} won last week at {track}. Can he do it twice?', 'Last week belonged to {N} at {track}.'],
      slots: { N: lw.winnerName, track: spokenTrack(lw.trackName) },
    });
  }

  // ---- an exhibition, or a league with nothing to say yet: the grid -------
  if (drafts.length === 0 && pre.order.length >= 2) {
    const [p1, p2] = pre.order;
    drafts.push({
      key: `pole|${p1}`, kind: 'pole', ids: [p1, p2], weight: 1,
      facts: [fact('race-field', `${name(p1)} on the pole, ${name(p2)} second`, 1, [p1, p2])],
      bank: ['{P} has the pole, {Q} alongside him.', '{P} leads them to the green from the pole, with {Q} on the front row.'],
      slots: { P: name(p1), Q: name(p2) },
    });
  }

  // ---- choose ------------------------------------------------------------
  // Weighted, then a small hash so equal weights rotate week to week.
  const ranked = drafts
    .map((dr) => ({ dr, score: dr.weight + (stableHash(`story|${league?.key ?? ''}|${seq}|${dr.key}`) % 100) / 60 }))
    .sort((a, b) => b.score - a.score);
  const chosen: Storyline[] = [];
  const kinds = new Set<StoryKind>();
  const people = new Set<string>();
  for (const { dr } of ranked) {
    if (chosen.length >= max) break;
    if (kinds.has(dr.kind)) continue;
    if (dr.ids.some((id) => people.has(id))) continue;
    const t = rotate(dr.bank, `open|${league?.key ?? ''}|${dr.key}`, seq);
    let text = fillSlots(t.item, dr.slots);
    if (dr.extra) text += ` ${dr.extra}`;
    chosen.push({ key: dr.key, kind: dr.kind, ids: dr.ids, weight: dr.weight, n: dr.n, facts: dr.facts, text, tpl: `story-${dr.kind}#${t.index}` });
    kinds.add(dr.kind);
    dr.ids.forEach((id) => people.add(id));
  }
  return chosen;
}

// ---------------------------------------------------------------------------
// The wrap — reads the finished race only
// ---------------------------------------------------------------------------

export function resolveStoryline(
  s: Storyline,
  finish: readonly FinishEntry[],
  winnerId: string | null,
  key: string,
  seq: number,
  shortOf?: (id: string) => string,
): { text: string; facts: Fact[]; tpl: string } | null {
  const at = (id: string) => finish.find((f) => f.driverId === id);
  const short = (f: FinishEntry) => (shortOf ? shortOf(f.driverId) : surname(f.name));
  const pos = (id: string) => {
    const f = at(id);
    if (!f) return null;
    return f.status === 'out' ? 'out of the race' : ordWord(f.place);
  };
  const nm = (id: string) => at(id)?.name ?? id;
  const facts: Fact[] = s.ids
    .map((id) => at(id))
    .filter((f): f is FinishEntry => !!f)
    .map((f) => fact('race-status', `${f.name} finished ${f.place}${f.status === 'out' ? ' (out of the race)' : ''}`, f.place, [f.driverId]));
  const pick = (bank: string[]) => rotate(bank, `wrap|${key}|${s.key}`, seq);
  const winner = (id: string) => winnerId === id;
  const [a, b] = s.ids;
  let bank: string[] = [];
  let slots: Record<string, string | number> = {};
  const out = (id: string) => at(id)?.status === 'out';
  /** "ran fifth" / "didn't finish" — a verb phrase true either way. */
  const did = (id: string) => (out(id) ? "didn't finish" : `ran ${pos(id)}`);
  switch (s.kind) {
    case 'rivalry': {
      if (!a || !b || !at(a) || !at(b)) return null;
      const fa = at(a) as FinishEntry;
      const fb = at(b) as FinishEntry;
      const [w, l] = fa.place < fb.place ? [fa, fb] : [fb, fa];
      slots = { a: short(fa), b: short(fb), w: short(w), l: short(l), W: w.name, L: l.name, wp: pos(w.driverId) as string, lp: pos(l.driverId) as string, ap: pos(fa.driverId) as string, bp: pos(fb.driverId) as string };
      if (out(w.driverId) && out(l.driverId)) bank = ['Neither {a} nor {b} made the finish.', 'As for {a} and {b}: both of them out of the race.'];
      else if (out(l.driverId)) bank = ["{W} ran {wp}, and {L} didn't finish. That one goes to {w}.", "{L} didn't make the finish; {W} ran {wp}. Round to {w}.", 'Score this one for {w}: {wp}, with {l} out of the race.'];
      else bank = ['As for {a} and {b}: {w} got the better of it today, {wp} to {lp}.', '{W} ran {wp}, {L} {lp}. Round to {w} in that one.', "The rivalry: {w} finished {wp}, {l} {lp}. That won't settle anything.", 'Score this one for {w}: {wp}, with {l} {lp}.', '{a} and {b} came out of it {ap} and {bp}. Bragging rights to {w}.', '{W} beat {L} today, {wp} to {lp}. Somebody will remember that.'];
      break;
    }
    case 'first-win':
    case 'drought':
      if (!a || !at(a)) return null;
      if (winner(a)) return null; // the win itself is the finish call
      slots = { N: nm(a), p: pos(a) as string, P: cap(pos(a) as string) };
      if (out(a)) bank = s.kind === 'first-win' ? ["No first win for {N}. He didn't make the finish.", '{N} was out of the race, and the wait for a first win goes on.'] : ["{N} didn't finish, and the drought goes on.", 'The drought rolls on for {N}, out of the race today.'];
      else bank = s.kind === 'first-win'
        ? ['{N} ran {p}. The wait for a first win goes on.', 'No first win today for {N}. He finished {p}.', '{N} came home {p}, still looking for that first one.', '{N}, still chasing a first win, ran {p}.', 'One more start without a win for {N}, who finished {p}.']
        : ['{N} finished {p}, and the drought goes on.', 'Still no win for {N}, {p} today.', '{N} ran {p}. The wait continues.', 'The drought rolls on for {N}. He was {p}.', '{N} was {p}, and he is still looking for the next one.'];
      break;
    case 'streak':
      if (!a || !at(a)) return null;
      if (winner(a)) return null;
      bank = ['The streak ends at {n} for {N}. He {did}.', 'That is the end of the streak for {N} at {n} straight. He {did}.'];
      slots = { N: nm(a), n: s.n ?? 0, did: did(a) };
      facts.push(fact('history', `${nm(a)} had won ${s.n} in a row`, s.n ?? 0, [a]));
      break;
    case 'points':
      if (!a || !b || !at(a) || !at(b)) return null;
      bank = ['In the points fight, {L} {ld} and {S} {sd}.', 'The two at the top of the standings: {L} {ld}, {S} {sd}.', '{L}, the points leader coming in, {ld}. {S} {sd}.'];
      slots = { L: nm(a), S: nm(b), ld: did(a), sd: did(b) };
      break;
    case 'bubble':
      if (!a || !b || !at(a) || !at(b)) return null;
      bank = ['On the playoff bubble, {In} {id} and {Out} {od}.', 'The cut-line fight: {Out} {od}, {In} {id}.'];
      slots = { In: nm(a), Out: nm(b), id: did(a), od: did(b) };
      break;
    case 'playoff':
      if (!a || !at(a)) return null;
      bank = ['{N}, on the cut line coming in, {d}.', 'The man on the playoff bubble, {N}, {d}.'];
      slots = { N: nm(a), d: did(a) };
      break;
    case 'defending':
    case 'track':
    case 'champion':
    case 'last-week':
    case 'milestone':
      if (!a || !at(a) || winner(a)) return null;
      slots = { N: nm(a), d: did(a), p: pos(a) as string };
      bank = s.kind === 'defending'
        ? ['The defending winner here, {N}, {d}.', "{N} couldn't defend this one. He {d}."]
        : s.kind === 'track'
          ? ['{N}, a winner here before, {d}.', 'No win this time at a track he has won at before. {N} {d}.']
          : s.kind === 'champion'
            ? ['The champion, {N}, {d}.', '{N}, the defending champion, {d}.']
            : s.kind === 'last-week'
              ? (out(a) ? ["Last week's winner, {N}, didn't finish this one."] : ['{N} followed up last week with a {p}-place run.', "Last week's winner, {N}, ran {p}."])
              : ['{N} will have to wait for that milestone. He {d}.', 'No milestone win for {N} today. He {d}.'];
      break;
    case 'pole':
      if (!a || !at(a) || winner(a)) return null;
      bank = ['The pole sitter, {N}, {d}.', '{N} started on the pole and {d}.'];
      slots = { N: nm(a), d: did(a) };
      break;
  }
  if (!bank.length) return null;
  const t = pick(bank);
  const text = cap(fillSlots(t.item, slots));
  return { text, facts, tpl: `wrap-${s.kind}#${t.index}` };
}

/** A rivalry record for two drivers, cut at the league's previous race. */
export function recordFor(league: BoothLeague | null | undefined, a: string, b: string): RivalryRecord | null {
  if (!league?.people) return null;
  return rivalryBetween(league.people.book, a, b);
}

export { ordWord };
